import type { SessionInfo } from "@zentrani/api-client";

/**
 * Seam between the shared web code and the native (Capacitor) shell.
 *
 * Nothing here imports Capacitor: the web build leaves every hook unset, so web behavior is
 * unchanged. The native build fills the hooks in src/native/bootstrap.ts before rendering.
 */

/** True for `VITE_PLATFORM=native` builds (the Android shell); constant-folded by Vite. */
export const NATIVE_BUILD = import.meta.env.VITE_PLATFORM === "native";

type NativeHooks = {
  /** Session ended (logout or rejected token): forget the stored token. */
  onSessionCleared?: () => void;
  /** Session moved to another community (Ajustes): point the client at its host. */
  onCommunityChanged?: (slug: string) => Promise<void>;
  /** Public origin for links shared with visitors (the community's web host). */
  publicOrigin?: () => string | null;
  /** Non-production native build (staging flavor): show debug aids such as scan timing. */
  debugTools?: boolean;
  /** Full-screen native UI (scanner) closes on the hardware back button before routing. */
  backOverride?: (() => void) | null;
  /** Session loaded or changed: cache the profile for offline launches. */
  onSessionLoaded?: (session: SessionInfo) => void;
  /** Session check failed without a 401 (offline): last cached profile, if any. */
  offlineSession?: () => Promise<SessionInfo | null>;
  /** Showing an access QR: keep the screen on at full brightness; returns the undo. */
  enterQrDisplay?: () => Promise<() => void>;
  /** Generated files (PDF reports): save + share sheet instead of a browser download. */
  saveBlob?: (blob: Blob, filename: string) => Promise<void>;
  /** Native push (FCM); `available` only when this app id had Firebase config at build time. */
  nativePush?: {
    available: boolean;
    isRegistered(): Promise<boolean>;
    enable(): Promise<void>;
    disable(): Promise<void>;
  };
  /** Before logout (session still valid): unregister this phone from push. */
  beforeLogout?: () => Promise<void>;
};

/** Save a jsPDF document: browser download on the web, share sheet in the app. */
export async function savePdf(
  doc: { save: (filename: string) => unknown; output: (type: "blob") => Blob },
  filename: string
): Promise<void> {
  if (nativeHooks.saveBlob) await nativeHooks.saveBlob(doc.output("blob"), filename);
  else doc.save(filename);
}

export const nativeHooks: NativeHooks = {};

/** True inside the Capacitor WebView (the bridge injects window.Capacitor). */
export function isCapacitorNative(): boolean {
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return Boolean(cap?.isNativePlatform?.());
}

/** Origin for absolute links shared outside the app (visitor access, events, share button). */
export function publicOrigin(): string {
  return nativeHooks.publicOrigin?.() ?? window.location.origin;
}
